import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Button } from '@mui/material';

import { DataGrid, Page, PageHeader } from '@/components';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import { useSearchPersist } from '@/components/DataGrid/hooks/useSearchPersist';
import {
  DataGridColSettings,
  mergeColumnSettings,
} from '@/components/DataGrid/utils';
import { LocalStoragePrefix } from '@/constants';
import { useBuildingInspectionContext } from '@/pages/Tools/BuildingInspection/BuildingInspectionContext';
import InspectionModal from '@/pages/Tools/BuildingInspection/modals/InspectionModal';
import { BuildingInspection } from '@/pages/Tools/BuildingInspection/Models';
import { useUserStore } from '@/store';
import { getLocalStorageItem } from '@/utils';

import { inspectionsDataGridCols } from '../dummy_data/dummy_data';

export const BuildingInspectionGrid = () => {
  const { user } = useUserStore();
  const { buildingId } = useParams();
  const { buildingRecords, inspections } = useBuildingInspectionContext();

  const currentBuilding = buildingRecords.find(
    (record) => record._id === buildingId,
  );
  const { navigateWithSearch, searchQuery } = useSearchPersist();
  const [openInspectionModal, setOpenInspectionModal] = useState<
    string | undefined
  >();

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      const savedSettings = getLocalStorageItem({
        key: String(user?._id),
        prefix: `building-inspection-list-${LocalStoragePrefix.TableSetting}`,
        shouldParse: true,
      });

      if (!savedSettings) {
        return inspectionsDataGridCols;
      }

      return mergeColumnSettings(inspectionsDataGridCols, savedSettings);
    },
  );

  const handleDelete = (e: string) => e;

  const handleEdit = (e: string) => {
    setOpenInspectionModal(e);
  };
  const columns = useDataGridColumns({
    type: 'buildingInspectionListCols',
    columnSettings,
    onDelete: handleDelete,
    onEdit: handleEdit,
  }).map((column) => ({
    ...column,
    renderCell: (params: any) => {
      if (column.field === 'action') {
        return column.renderCell?.({
          ...params,
          openInspectionModal,
        });
      }

      return column.renderCell ? column.renderCell(params) : params.value;
    },
  }));

  return (
    <Page title={currentBuilding?.name}>
      <PageHeader
        title={`${currentBuilding?.name} - ${currentBuilding?.streetAddress}, ${currentBuilding?.city} ${currentBuilding?.zipCode}`}
        backRoute={`/tools/building-inspection${searchQuery}`}
      >
        <Button
          variant="contained"
          color="error"
          size="medium"
          onClick={() => setOpenInspectionModal('new')}
        >
          Add inspection
        </Button>
      </PageHeader>
      <div className="h-[calc(100vh-150px)] overflow-auto pt-3">
        {/*<pre>*/}
        {/*  {JSON.stringify(inspections, null, 2)}*/}
        {/*</pre>*/}
        {inspections?.length && (
          <DataGrid
            key="buildingInspectionListCols"
            name="building-inspection-list"
            autoPageSize={false}
            persistSearch
            clientMode
            clientRows={inspections.filter(
              (inspection: BuildingInspection) =>
                inspection.building_id === buildingId,
            )}
            columnSettings={columnSettings}
            setColumnSettings={setColumnSettings}
            onRowClick={(params) =>
              navigateWithSearch(
                `/tools/building-inspection/${buildingId}/view_inspection/${params.row._id}`,
              )
            }
            columns={columns}
            disableMultipleColumnsSorting={true}
          />
        )}
      </div>

      <InspectionModal
        openModal={!!openInspectionModal}
        setOpenModal={setOpenInspectionModal}
      />
    </Page>
  );
};
